import { createAsyncThunk } from '@reduxjs/toolkit';
import jobApi from '~/apis/job';
import {
  GET_JOBS,
  GET_JOB,
  CREATE_JOB,
  GET_FILES,
  CREATE_FILES,
  GET_JOBS_BOARD,
  GET_JOBS_LIST_MORE,
  UPDATE_JOB,
  GET_NOTES,
  CREATE_NOTE,
  UPDATE_NOTE,
  GET_TEMPLATE,
  GET_FOLDERS,
  UPDATE_JOB_ALL,
  GET_JOBS_TIMELINE,
  GET_JOBS_SELECTION,
} from '~/utils/constants/actionType';
import { IJobFolderCreate, IResponseGetFolders } from '~/utils/interface/folder';
import {
  IEditJobPayload,
  IJobFileCreate,
  IParamsGetJobs,
  IParamsGetJobsBoard,
  IParamsGetJobsTimeline,
  IParamsTemplate,
  ICreateReportFile,
  IResponseGetFiles,
  IResponseGetJob,
  IResponseGetJobDetail,
  IResponseGetJobsBoard,
  IResponseGetJobsTimeline,
  IResponseGetNote,
  IResponseGetTemplate,
  IResponseGetJobSelection,
  IUpdateJobPayload,
} from '~/utils/interface/job';
import { IParamsGetNotes } from '~/utils/interface/note';
import { IEditNote } from '~/utils/interface/note';

export const handleGetJobsSelection = createAsyncThunk<IResponseGetJobSelection, string | undefined>(
  GET_JOBS_SELECTION,
  async (type: string | undefined, { rejectWithValue }) => {
    try {
      const res = await jobApi.getJobsSelection(type);
      return res.data.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const handleGetJobs = createAsyncThunk<IResponseGetJob, IParamsGetJobs>(
  GET_JOBS,
  async (params: IParamsGetJobs = {}, { rejectWithValue }) => {
    try {
      const res = await jobApi.getJobs(params);
      return res.data.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const handleGetJob = createAsyncThunk<IResponseGetJobDetail, string>(
  GET_JOB,
  async (id: string, { rejectWithValue }) => {
    try {
      const res = await jobApi.getJob(id);
      return res.data.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const handleCreateJob = createAsyncThunk<IResponseGetJobDetail, IEditJobPayload>(
  CREATE_JOB,
  async (data: IEditJobPayload, { rejectWithValue }) => {
    try {
      const res = await jobApi.create(data);
      return res.data.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const handleGetJobFiles = createAsyncThunk<IResponseGetFiles, { id: string | undefined; data: IJobFileCreate }>(
  GET_FILES,
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const res = await jobApi.getFiles(id, data);
      return res.data.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const handleCreateJobFiles = createAsyncThunk<null, { id: string | undefined; data: FormData }>(
  CREATE_FILES,
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const res = await jobApi.createFiles(id, data);
      return res.data.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const handleCreateJobUploadFiles = createAsyncThunk<null, { id: string | undefined; data: FormData }>(
  CREATE_FILES,
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const res = await jobApi.createUploadFiles(id, data);
      return res.data.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const handleGetJobsBoard = createAsyncThunk<IResponseGetJobsBoard, IParamsGetJobsBoard>(
  GET_JOBS_BOARD,
  async (params: IParamsGetJobsBoard = {}, { rejectWithValue }) => {
    try {
      const res = await jobApi.getJobsBoard(params);
      return res.data.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const handleGetJobsTimeline = createAsyncThunk<IResponseGetJobsTimeline, IParamsGetJobsTimeline>(
  GET_JOBS_TIMELINE,
  async (params: IParamsGetJobsTimeline = {}, { rejectWithValue }) => {
    try {
      const res = await jobApi.getJobsTimeline(params);
      return res.data.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const handleGetJobsList = createAsyncThunk<IResponseGetJob, IParamsGetJobs>(
  GET_JOBS_LIST_MORE,
  async (params: IParamsGetJobs = {}, { rejectWithValue }) => {
    try {
      const res = await jobApi.getJobs(params);
      return res.data.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const handleUpdateJobAll = createAsyncThunk<IResponseGetJobDetail, { id: number; params: IEditJobPayload }>(
  UPDATE_JOB_ALL,
  async ({ id, params }, { rejectWithValue }) => {
    try {
      const res = await jobApi.updateJobAll(id, params);
      return res.data.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const handleUpdateJob = createAsyncThunk<IResponseGetJobDetail, { id: number; params: IUpdateJobPayload }>(
  UPDATE_JOB,
  async ({ id, params }, { rejectWithValue }) => {
    try {
      const res = await jobApi.updateJob(id, params);
      return res.data.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const handleGetNoteList = createAsyncThunk<IResponseGetNote, IParamsGetNotes>(
  GET_NOTES,
  async (params: IParamsGetNotes, { rejectWithValue }) => {
    try {
      const res = await jobApi.getNotes(params);
      return res.data.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const handleCreateNote = createAsyncThunk<null, IEditNote>(
  CREATE_NOTE,
  async (data: IEditNote, { rejectWithValue }) => {
    try {
      const res = await jobApi.createNotes(data);
      return res.data.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const handleUpdateNotes = createAsyncThunk<IResponseGetNote, { id: number | string; params: IEditNote }>(
  UPDATE_NOTE,
  async ({ id, params }, { rejectWithValue }) => {
    try {
      const res = await jobApi.updateNotes(id, params);
      return res.data.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const handleGetTemplate = createAsyncThunk<IResponseGetTemplate, { params: IParamsTemplate }>(
  GET_TEMPLATE,
  async ({ params }, { rejectWithValue }) => {
    try {
      const res = await jobApi.getTemplate(params);
      return res.data.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const handleGetJobFolders = createAsyncThunk<
  IResponseGetFolders,
  { id: string | undefined; data: IJobFolderCreate }
>(GET_FOLDERS, async ({ id, data }, { rejectWithValue }) => {
  try {
    const res = await jobApi.getFolders(id, data);
    return res.data.data;
  } catch (error) {
    return rejectWithValue(error);
  }
});

export const handleCreateReportFile = createAsyncThunk<null, { id: string; params: ICreateReportFile }>(
  CREATE_NOTE,
  async ({ id, params }, { rejectWithValue }) => {
    try {
      const res = await jobApi.createReportFiles(id, params);
      return res.data.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);
